import { Helmet } from "react-helmet-async";
import useGetDenseOrderSheetSet, {
  GetDenseOrderSheetSetPathParameter,
} from "@/features/order-sheet-sets/hooks/use-get-dense-order-sheet-set";
import useIdentity from "@/features/ui/hooks/use-identity";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Spacer,
  Switch,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import AppIconButton from "@/features/line-sheet-sets/app-icon-button";
import { useParams } from "react-router";
import AppButton from "@/features/line-sheet-sets/app-button";
import {
  DenseOrderSheet,
  DenseOrderSheetTab,
  isOrderedToAgency,
  OrderSheetSet,
} from "@/features/order-sheets/order-sheet.type";
import { useRecoilState, useRecoilValue } from "recoil";
import OrderSheetSetSaveButton from "@/features/order-sheet-sets/order-sheet-set-save-button";
import OrderSheetOrLineSheetSelector from "@/features/order-sheet-sets/order-sheet-or-line-sheet-selector";
import AppTabSelect from "@/features/line-sheet-sets/app-tap-select";
import { LightLineSheet } from "@/features/line-sheet-sets/line-sheet-set.types";
import useCreateOrderSheet from "@/features/line-sheet-sets/hooks/use-create-order-sheet";
import { SubmitOrderSheetSetResponse } from "@/features/order-sheet-sets/hooks/use-submit-order-sheet-set";
import {
  isBulkUpdateOrderSheetStatusResponse,
  OrderSheetStatusUpdaterProps,
  UpdateOrderSheetStatusItem,
} from "@/features/order-status/order-sheet-status-updater";
import useAppDialog from "@/features/ui/hooks/use-app-dialog";
import { CreateOrderSheetRevisionResponse } from "@/features/line-sheet-sets/hooks/use-create-order-sheet-revision";
import OrderSheetOrderButton from "@/features/order-sheet-sets/order-sheet-set-order-button";
import { getOrderMoq } from "@/features/order-sheet-sets/moq-notice-banner";
import { canOrderByUnit } from "@/features/line-sheet-sets/helpers/can-order-by-unit";
import OrderSheetAdjustPriceFragment from "@/features/order-sheet-sets/order-sheet-adjust-price-fragment";
import SheetTabFilter, {
  EMPTY_SHEET_TAB_FILTER_QUERY,
  isProductFiltered,
  SheetTabFilterQuery,
  useStableSheetTabFilterQuery,
} from "@/features/order-sheet-sets/sheet-tab-filter";
import useFetchAndSetOrderSheetTabs from "@/features/order-sheet-sets/hooks/use-fetch-and-set-order-sheet-tabs";
import useOrderSheetSetPagePersistedQuery from "@/features/order-sheet-sets/hooks/use-order-sheet-set-page-persisted-query";
import useInflatedTabs from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import { ColumnApi, GridApi, GridReadyEvent } from "ag-grid-community";
import { produce } from "immer";
import {
  clientSheetStatisticFamily,
  FlatProduct,
  FlatSheetKey,
  inflatedTabsFamily,
  SheetStatisticState,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import { UpdateLineSheetSetOrderRulesResponse } from "@/features/line-sheet-sets/hooks/use-update-line-sheet-set-order-rules";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import { useBeforeUnload } from "react-router-dom";
import LineSheetTab from "@/features/line-sheet-sets/line-sheet-tab";
import useListOrderSheetRevisions, {
  DEFAULT_LIST_REVISIONS_PAGE_SIZE,
  ListOrderSheetRevisionsPathParameter,
} from "./hooks/use-list-order-sheet-revisions";
import { OrderSheetRevision } from "./order-sheet-revision.type";
import { CompanyType } from "../types";
import useFetchAndSetLineSheetSet, {
  UseFetchAndSetLineSheetSetParameter,
} from "../line-sheet-sets/hooks/use-fetch-and-set-line-sheet-set";
import useBulkUpdateOrderSheetStatus from "../order-status/hooks/use-bulk-update-order-sheet-status";
import useListAllCompanies, {
  ListAllCompaniesPathParameter,
  ListAllCompaniesRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-companies";
import AppOrderSheetStatusLabel from "@/features/ui/app-order-sheet-status-label";
import SVG from "react-inlinesvg";
import CurrencySwitch from "@/shared/components/currency-switch";
import { isOrderSheetPageCurrencyKrw } from "@/features/order-sheet-sets/recoils/order-sheet-page-store";
import SheetInfoPanel from "@/features/line-sheet-sets/sheet-info-panel";
import OrderSheetTab from "@/features/order-sheet-sets/order-sheet-tab";
import AppLabel from "@/features/ui/app-label";
import AppSelect from "@/features/line-sheet-sets/app-select";
import AgencyOrderSheetEditConfirmInfo from "@/features/order-sheet-sets/agency-order-sheet-edit-confirm-info";
import AddLineSheetProductModal from "@/shared/add-line-sheet-product-modal";
import OrderStatusBadge from "@/features/order-status/OrderStatusBadge";
import { SubmitOrderSheetSetV2Response } from "@/features/order-sheet-sets/hooks/use-submit-order-sheet-set-v2";
import { getCanonicalUrl } from "@/shared/utils/canonical-url";

export function isDenseOrderSheet(
  value: DenseOrderSheet | LightLineSheet
): value is DenseOrderSheet {
  return (value as LightLineSheet).name === undefined;
}

export function isLineSheet(
  value: DenseOrderSheet | LightLineSheet
): value is LightLineSheet {
  return !isDenseOrderSheet(value);
}

function OrderSheetSetPage() {
  const identity = useIdentity();
  const company = useMemo(() => {
    return identity?.company;
  }, [identity]);
  const [isAddLineSheetProductOpen, setIsAddLineSheetProductOpen] =
    useState(false);
  const { orderSheetSetId: rawOrderSheetSetId } = useParams();
  const orderSheetSetId = useMemo(() => {
    let candidate;
    if (rawOrderSheetSetId) {
      candidate = parseInt(rawOrderSheetSetId);
    }
    return candidate || -1;
  }, [rawOrderSheetSetId]);
  const { t, tTitle, tSentence } = useI18nHelper();
  const [queryCount, setQueryCount] = useState(0);

  const { navigate } = useRouterHelper();
  const { error: showError } = useAppToasts();
  const { openDialog } = useAppDialog();
  const gridRef = useRef<{
    api: GridApi<FlatProduct>;
    columnApi: ColumnApi;
  } | null>(null);

  const handleGridReady = useCallback((event: GridReadyEvent<FlatProduct>) => {
    gridRef.current = {
      api: event.api,
      columnApi: event.columnApi,
    };
  }, []);

  const getDenseOrderSheetSetParameter = useMemo(():
    | GetDenseOrderSheetSetPathParameter
    | undefined => {
    if (company && orderSheetSetId > -1) {
      return {
        by: company.type,
        companyId: company.id,
        orderSheetSetId,
      };
    }
  }, [company, orderSheetSetId]);

  const {
    isLoading: isGetDenseOrderSheetSetLoading,
    isValidating: isGetDenseOrderSheetSetValidating,
    error: getDenseOrderSheetSetError,
    data: getDenseOrderSheetSetData,
    mutate: mutateGetDenseOrderSheetSet,
  } = useGetDenseOrderSheetSet(
    getDenseOrderSheetSetParameter,
    {},
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  useEffect(() => {
    showError(getDenseOrderSheetSetError);
  }, [showError, getDenseOrderSheetSetError]);

  const lineSheetSetId = useMemo(() => {
    if (getDenseOrderSheetSetData) {
      return getDenseOrderSheetSetData.lineSheetSet.id;
    }
    return -1;
  }, [getDenseOrderSheetSetData]);

  const getLineSheetSetParameter =
    useMemo((): UseFetchAndSetLineSheetSetParameter => {
      if (lineSheetSetId > 0) {
        return {
          data: {
            lineSheetSetId: lineSheetSetId,
          },
        };
      }
      return {
        data: undefined,
      };
    }, [lineSheetSetId]);

  const { getLineSheetSetError, getLineSheetSetData, mutateGetLineSheetSet } =
    useFetchAndSetLineSheetSet(getLineSheetSetParameter);

  //order-sheet-set-select -->
  const { value: query, set: setQuery } = useOrderSheetSetPagePersistedQuery();

  // 상품 필터/정렬에 실제로 쓰이는 필드만 담은 안정적인 객체.
  // referer, revision_number 등 무관한 URL 변경으로 전 상품 재계산이 도는 것을 막는다.
  const sheetTabFilterQuery = useStableSheetTabFilterQuery(query);

  const selectedOrderSheetOrLineSheet = useMemo(() => {
    if (getDenseOrderSheetSetData) {
      if (query.orderSheetId) {
        return getDenseOrderSheetSetData.orderSheets.find(
          (orderSheet) => orderSheet.id === query.orderSheetId
        );
      }
      if (query.lineSheetId) {
        return getDenseOrderSheetSetData.lineSheetSet.lineSheets.find(
          (lineSheet) => lineSheet.id === query.lineSheetId
        );
      }
    }
  }, [getDenseOrderSheetSetData, query.orderSheetId, query.lineSheetId]);

  const orderSheetOrLineSheetSelectValues = useMemo((): (
    | DenseOrderSheet
    | LightLineSheet
  )[] => {
    if (getDenseOrderSheetSetData) {
      const lineSheetSet = getDenseOrderSheetSetData.lineSheetSet;
      const options = lineSheetSet.lineSheets
        .flatMap((lineSheet): (DenseOrderSheet | LightLineSheet)[] => {
          const orderSheets = getDenseOrderSheetSetData.orderSheets.filter(
            (orderSheet) => {
              return orderSheet.lineSheet.id === lineSheet.id;
            }
          );

          if (orderSheets.length > 0) {
            return [...orderSheets];
          } else {
            return [{ ...lineSheet }];
          }
        })
        .filter((item) => {
          let lineSheet;
          if (isLineSheet(item)) {
            if (company?.type === "AGENCY") {
              return false;
            }
            // else if (
            //   company?.types === "BUYER" &&
            //   isOrderedToAgency(getDenseOrderSheetSetData)
            // ) {
            //   return false;
            // }
            lineSheet = item;
          } else {
            lineSheet = item.lineSheet;
          }

          return lineSheet.status === "PARSED";
        });

      const getOrder = (item: DenseOrderSheet | LightLineSheet) => {
        if (isDenseOrderSheet(item)) {
          if (item.orderedQuantity > 0) {
            return 1;
          } else {
            return 2;
          }
        } else {
          return 2;
        }
      };

      options.sort((a, b) => {
        return getOrder(a) - getOrder(b);
      });

      // if (isOrderedToAgency(getDenseOrderSheetSetData)) {
      //   options.sort((left, right) => {
      //     if (isDenseOrderSheet(left) && isLineSheet(right)) {
      //       return -1;
      //     } else if (isLineSheet(left) && isDenseOrderSheet(right)) {
      //       return 1;
      //     } else {
      //       return 0;
      //     }
      //   });
      // }

      return options;
    }
    return [];
  }, [getDenseOrderSheetSetData, company]);

  const orderSheetTabOrLineSheetTabSelectValues = useMemo(() => {
    if (getDenseOrderSheetSetData && selectedOrderSheetOrLineSheet) {
      if (isDenseOrderSheet(selectedOrderSheetOrLineSheet)) {
        return selectedOrderSheetOrLineSheet.orderSheetTabs;
      } else {
        return selectedOrderSheetOrLineSheet.lineSheetTabs;
      }
    }
    return [];
  }, [selectedOrderSheetOrLineSheet, getDenseOrderSheetSetData]);

  const selectedOrderSheetTabOrLineSheetTab = useMemo(() => {
    if (selectedOrderSheetOrLineSheet) {
      if (isDenseOrderSheet(selectedOrderSheetOrLineSheet)) {
        return selectedOrderSheetOrLineSheet.orderSheetTabs.find(
          (i) => i.index === query.orderSheetTabIndex
        );
      } else if (isLineSheet(selectedOrderSheetOrLineSheet)) {
        return selectedOrderSheetOrLineSheet.lineSheetTabs.find(
          (i) => i.index == query.lineSheetTabIndex
        );
      }
    }
  }, [
    selectedOrderSheetOrLineSheet,
    query.orderSheetTabIndex,
    query.lineSheetTabIndex,
  ]);

  const currency = useMemo(() => {
    if (selectedOrderSheetOrLineSheet) {
      if (isDenseOrderSheet(selectedOrderSheetOrLineSheet)) {
        return selectedOrderSheetOrLineSheet.lineSheet.currency;
      } else if (isLineSheet(selectedOrderSheetOrLineSheet)) {
        return selectedOrderSheetOrLineSheet.currency;
      }
    }
  }, [selectedOrderSheetOrLineSheet]);

  //--> order-sheet-set-select

  /* order-sheet-revision-selector */

  const getOrderSheetRevisionsParameter =
    useMemo((): ListOrderSheetRevisionsPathParameter => {
      if (
        identity &&
        identity.company &&
        selectedOrderSheetOrLineSheet &&
        orderSheetSetId > -1 &&
        getDenseOrderSheetSetData &&
        isDenseOrderSheet(selectedOrderSheetOrLineSheet)
      ) {
        return {
          orderSheetSetId: orderSheetSetId,
          orderSheetId: selectedOrderSheetOrLineSheet.id,
          by: identity.company.type,
          companyId: identity.company.id,
        };
      }
      return {
        orderSheetSetId: -1,
        orderSheetId: -1,
        by: "BUYER" as CompanyType,
        companyId: -1,
      };
    }, [
      selectedOrderSheetOrLineSheet,
      orderSheetSetId,
      getDenseOrderSheetSetData,
      identity,
    ]);

  const {
    isLoading: isGetOrderSheetRevisionsLoading,
    isValidating: isGetOrderSheetRevisionsValidating,
    error: getOrderSheetRevisionsError,
    data: getOrderSheetRevisionsData,
    mutate: mutateGetOrderSheetRevisions,
  } = useListOrderSheetRevisions(
    getOrderSheetRevisionsParameter,
    {
      pageNumber: 1,
      pageSize: DEFAULT_LIST_REVISIONS_PAGE_SIZE,
      submitted__eq: true,
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  const latestRevision = useMemo(() => {
    if (
      getOrderSheetRevisionsData &&
      getOrderSheetRevisionsData.revisions &&
      getOrderSheetRevisionsData.revisions.length > 0
    ) {
      return getOrderSheetRevisionsData.revisions.reduce(
        (prev: OrderSheetRevision, current: OrderSheetRevision) => {
          return prev.revisionNumber > current.revisionNumber ? prev : current;
        }
      );
    }
    return {
      revisionNumber: -1,
      createdAt: new Date(),
      createdBy: {
        id: -1,
        name: "",
        email: "",
      },
      createdByCompany: {
        id: -1,
        name: "",
        type: "BUYER" as CompanyType,
      },
    };
  }, [getOrderSheetRevisionsData]);

  const selectedOrderSheetRevision = useMemo(() => {
    if (
      getOrderSheetRevisionsData &&
      getOrderSheetRevisionsData.revisions.length > 0 &&
      typeof query.revisionNumber === "number" &&
      query.revisionNumber > 0
    ) {
      const revision = getOrderSheetRevisionsData.revisions.find(
        (revision) => revision.revisionNumber === query.revisionNumber
      );
      if (revision) {
        return revision;
      }
    }
    return latestRevision;
  }, [getOrderSheetRevisionsData, query.revisionNumber, latestRevision]);

  const orderSheetRevisionSelectValues = useMemo((): OrderSheetRevision[] => {
    if (getOrderSheetRevisionsData) {
      const revisions = getOrderSheetRevisionsData.revisions;
      if (revisions.length > 0) {
        return revisions;
      }
    }
    return [];
  }, [getOrderSheetRevisionsData]);

  const brandKey = useMemo(():
    | {
        parameter: ListAllCompaniesPathParameter;
        request: ListAllCompaniesRequest;
      }
    | undefined => {
    if (identity?.company) {
      let request: ListAllCompaniesRequest = {};
      request.type__in = ["BRAND"];
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request,
      };
    }
    return undefined;
  }, [identity]);

  const { data: companiesData } = useListAllCompanies(
    brandKey?.parameter,
    brandKey?.request
  );

  // made_by_company.id__in -> brand_name__eq 정규화가 이번 커밋에 실행될 예정인지.
  // 같은 커밋에서 setQuery를 두 번 호출하면 react-router의 setSearchParams가
  // 렌더 시점 스냅샷을 기준으로 계산하므로 뒤쪽 호출이 앞쪽 결과를 통째로 덮어쓴다.
  // (목록 -> 주문서 재진입처럼 SWR 캐시가 전부 히트일 때 브랜드 필터가 사라지던 원인)
  const isBrandFilterNormalizationPending =
    (query["madeByCompany.id__in"]?.length ?? 0) > 0 && !!companiesData;

  useEffect(() => {
    const madeByCompanyIds = query["madeByCompany.id__in"];
    if (madeByCompanyIds && madeByCompanyIds.length > 0 && companiesData) {
      setQuery(
        (prev) => {
          return {
            ...prev,
            "madeByCompany.id__in": undefined,
            brandName__eq: madeByCompanyIds
              .map(
                (id) => companiesData.find((company) => company.id === id)?.name
              )
              .filter((company): company is string => company !== undefined),
          };
        },
        {
          replace: true,
        }
      );
    }
  }, [query, companiesData, setQuery]);

  //기본값 채워주기
  useEffect(() => {
    // 브랜드 필터 정규화가 끝난 다음 커밋에서 채운다. 같은 커밋에서 함께 쓰면
    // 이쪽 setQuery가 정규화 결과(brand_name__eq)를 덮어써 브랜드 필터가 유실된다.
    if (isBrandFilterNormalizationPending) return;
    if (selectedOrderSheetOrLineSheet && getDenseOrderSheetSetData) {
      if (
        getOrderSheetRevisionsData &&
        isDenseOrderSheet(selectedOrderSheetOrLineSheet) &&
        query.orderSheetTabIndex === undefined
      ) {
        setQuery(
          (prev) => {
            return {
              ...prev,
              orderSheetTabIndex:
                selectedOrderSheetOrLineSheet.orderSheetTabs.length > 0
                  ? selectedOrderSheetOrLineSheet.orderSheetTabs[0].index
                  : undefined,
              revisionNumber: latestRevision.revisionNumber,
            };
          },
          {
            replace: true,
          }
        );
      } else if (
        isOrderedToAgency(getDenseOrderSheetSetData) &&
        isLineSheet(selectedOrderSheetOrLineSheet) &&
        query.lineSheetTabIndex === undefined
      ) {
        setQuery(
          (prev) => {
            return {
              ...prev,
              lineSheetTabIndex:
                selectedOrderSheetOrLineSheet.lineSheetTabs.length > 0
                  ? selectedOrderSheetOrLineSheet.lineSheetTabs[0].index
                  : undefined,
            };
          },
          {
            replace: true,
          }
        );
      }
    }
  }, [
    selectedOrderSheetOrLineSheet,
    query,
    setQuery,
    getDenseOrderSheetSetData,
    getOrderSheetRevisionsData,
    latestRevision,
    isBrandFilterNormalizationPending,
  ]);

  const hasInitializedQuantityFilter = useRef(false);
  useEffect(() => {
    // 정규화와 같은 커밋에서 쓰면 brand_name__eq가 덮어써진다. ref를 세우기 전에 빠져나가
    // 다음 커밋에서 다시 초기화될 수 있게 한다.
    if (isBrandFilterNormalizationPending) return;
    if (
      !hasInitializedQuantityFilter.current &&
      (identity?.company?.type === "AGENCY" ||
        identity?.company?.type === "BOUTIQUE") &&
      query.quantity__gte === undefined
    ) {
      hasInitializedQuantityFilter.current = true;
      setQuery((prev) => ({ ...prev, quantity__gte: 1 }), { replace: true });
    }
  }, [
    identity,
    query.quantity__gte,
    setQuery,
    isBrandFilterNormalizationPending,
  ]);

  //--> order-sheet-revision-selector

  const { filterInflatedProducts, resetInflatedSheet } = useInflatedTabs();

  const _selectedOrderSheetId = useMemo(() => {
    if (
      selectedOrderSheetOrLineSheet &&
      isDenseOrderSheet(selectedOrderSheetOrLineSheet)
    ) {
      return selectedOrderSheetOrLineSheet.id;
    }
    return -1;
  }, [selectedOrderSheetOrLineSheet]);

  const sheetKey = useMemo((): FlatSheetKey => {
    if (getDenseOrderSheetSetData && selectedOrderSheetOrLineSheet) {
      if (
        isOrderedToAgency(getDenseOrderSheetSetData) &&
        isLineSheet(selectedOrderSheetOrLineSheet)
      ) {
        return {
          lineSheetSetId: getDenseOrderSheetSetData.lineSheetSet.id,
          lineSheetId: selectedOrderSheetOrLineSheet.id,
        };
      } else if (getOrderSheetRevisionsData) {
        return {
          orderSheetSetId: getDenseOrderSheetSetData.id,
          orderSheetId: selectedOrderSheetOrLineSheet.id,
          revisionNumber: selectedOrderSheetRevision.revisionNumber,
        };
      } else {
        return {
          orderSheetSetId: getDenseOrderSheetSetData.id,
          orderSheetId: selectedOrderSheetOrLineSheet.id,
          revisionNumber: -1,
        };
      }
    }

    return {
      orderSheetSetId: -1,
      orderSheetId: -1,
      revisionNumber: -1,
    };
  }, [
    getDenseOrderSheetSetData,
    selectedOrderSheetOrLineSheet,
    getOrderSheetRevisionsData,
    selectedOrderSheetRevision,
  ]);

  const [isOrderSheetEditing, setIsOrderSheetEditing] =
    useState<boolean>(false);

  const [inflatedTabs, setInflatedTabs] = useRecoilState(
    inflatedTabsFamily(sheetKey)
  );

  const sheetStatisticState = useRecoilValue(
    clientSheetStatisticFamily(sheetKey)
  );

  const [initialSheetStatistics, setInitialSheetStatistics] =
    useState<SheetStatisticState>();

  useEffect(() => {
    if (initialSheetStatistics === undefined && sheetStatisticState) {
      setInitialSheetStatistics(sheetStatisticState);
    }
  }, [initialSheetStatistics, sheetStatisticState]);

  const isOrderSheetDirty = useMemo(() => {
    if (inflatedTabs) {
      const dirty = inflatedTabs.tabs.find((i) => i.isDirty) !== undefined;
      if (dirty) {
        /* TODO: 수정이 발생했다면, 통계를 업데이트해주어야 함.
         * ag grid 에서 input에 값 입력을 시작하기만 했어도 업데이트를 해주어야 함.
         * 그래야 key in 했을 때 바로 초기화/저장/주문 버튼을 활성화시켜줄 수 있음.
         * order-sheet-tab.tsx에서 AG Grid 에서 onCellKeyDown 이벤트 호출되자마자 sheet tab의 isDirty를 true로 바꿔버리는 식으로 대응해둠.
         * isDirty가 true라고 해도 실제 데이터가 업데이트 된 상태는 아니므로,
         * 실제 데이터를 inflatedTabs에 반영해준 뒤에 sheet statistics를 업데이트해주어야 함.
         */
      }
      return dirty;
    }
    return false;
  }, [inflatedTabs]);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isOrderSheetEditing || isOrderSheetDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    [isOrderSheetEditing, isOrderSheetDirty]
  );

  useBeforeUnload(handleBeforeUnload);

  const confirmDiscardChanges = useCallback(() => {
    if (isOrderSheetEditing || isOrderSheetDirty) {
      return openDialog({
        title: tTitle("common:discard_changes"),
        message: t("common:discard_changes.message"),
        confirmName: tTitle("continue"),
      });
    }
    return Promise.resolve(true);
  }, [isOrderSheetDirty, isOrderSheetEditing, openDialog, t, tTitle]);

  const handleSelectOrderSheetOrLineSheet = useCallback(
    async (value?: DenseOrderSheet | LightLineSheet) => {
      if (selectedOrderSheetOrLineSheet === value) {
        return;
      }

      if (value) {
        if (isOrderSheetEditing || isOrderSheetDirty) {
          const result = await confirmDiscardChanges();

          if (!result) {
            return false;
          }
        }
        if (isDenseOrderSheet(value)) {
          setQuery(
            (prev) => {
              if (prev.orderSheetId !== undefined) {
                resetInflatedSheet({
                  orderSheetSetId,
                  orderSheetId: prev.orderSheetId,
                  revisionNumber: prev.revisionNumber,
                });
              }

              return {
                ...prev,
                orderSheetId: value.id,
                orderSheetTabIndex:
                  value.orderSheetTabs.length > 0
                    ? value.orderSheetTabs[0].index
                    : undefined,
                revisionNumber: undefined,
                lineSheetTabIndex: undefined,
                lineSheetId: undefined,
                ...EMPTY_SHEET_TAB_FILTER_QUERY,
                quantity__gte: prev.quantity__gte,
              };
            },
            { replace: true }
          );
        } else {
          setQuery(
            (prev) => {
              return {
                ...prev,
                orderSheetId: undefined,
                orderSheetTabIndex: undefined,
                revisionNumber: undefined,
                lineSheetTabIndex:
                  value.lineSheetTabs.length > 0
                    ? value.lineSheetTabs[0].index
                    : undefined,
                lineSheetId: value.id,
                ...EMPTY_SHEET_TAB_FILTER_QUERY,
                quantity__gte: prev.quantity__gte,
              };
            },
            { replace: true }
          );
        }
      }
    },
    [
      selectedOrderSheetOrLineSheet,
      isOrderSheetEditing,
      isOrderSheetDirty,
      confirmDiscardChanges,
      setQuery,
      resetInflatedSheet,
      orderSheetSetId,
    ]
  );

  const handleSelectOrderSheetRevision = useCallback(
    async (value?: OrderSheetRevision) => {
      if (selectedOrderSheetRevision === value) {
        return;
      }

      if (value) {
        if (isOrderSheetEditing || isOrderSheetDirty) {
          const result = await confirmDiscardChanges();

          if (!result) {
            return false;
          }
        }
        setQuery(
          (prev) => {
            return {
              ...prev,
              revisionNumber: value.revisionNumber,
            };
          },
          { replace: true }
        );
      }
    },
    [
      selectedOrderSheetRevision,
      isOrderSheetEditing,
      isOrderSheetDirty,
      setQuery,
      confirmDiscardChanges,
    ]
  );

  const handleSelectOrderSheetTabOrLineSheetTab = useCallback(
    (value?: DenseOrderSheetTab) => {
      if (getDenseOrderSheetSetData && selectedOrderSheetOrLineSheet) {
        if (isDenseOrderSheet(selectedOrderSheetOrLineSheet)) {
          setQuery((prev) => {
            return {
              ...prev,
              orderSheetTabIndex: value?.index,
              ...EMPTY_SHEET_TAB_FILTER_QUERY,
              quantity__gte: prev.quantity__gte,
            };
          });
        } else if (
          isOrderedToAgency(getDenseOrderSheetSetData) &&
          isLineSheet(selectedOrderSheetOrLineSheet)
        ) {
          setQuery((prev) => {
            return {
              ...prev,
              lineSheetTabIndex: value?.index,
              ...EMPTY_SHEET_TAB_FILTER_QUERY,
              quantity__gte: prev.quantity__gte,
            };
          });
        }
      }
    },
    [getDenseOrderSheetSetData, selectedOrderSheetOrLineSheet, setQuery]
  );

  const {
    isLoading: isCreateOrderSheetLoading,
    data: createOrderSheetData,
    error: createOrderSheetError,
    fire: fireCreateOrderSheet,
    clear: clearCreateOrderSheet,
  } = useCreateOrderSheet();

  useEffect(() => {
    showError(createOrderSheetError);
  }, [showError, createOrderSheetError]);

  useEffect(() => {
    if (
      !isCreateOrderSheetLoading &&
      createOrderSheetData === undefined &&
      createOrderSheetError === undefined &&
      selectedOrderSheetOrLineSheet &&
      isLineSheet(selectedOrderSheetOrLineSheet) &&
      getDenseOrderSheetSetData &&
      company
    ) {
      fireCreateOrderSheet(
        {
          orderSheetSetId: getDenseOrderSheetSetData.id,
          by: company.type,
          companyId: company.id,
          //_lineSheetSetId: getDenseOrderSheetSetData.lineSheetSet.id,
          //_lineSheetId: selectedOrderSheetOrLineSheet.id,
        },
        {
          lineSheet: {
            id: selectedOrderSheetOrLineSheet.id,
          },
        }
      );
    }
  }, [
    isCreateOrderSheetLoading,
    createOrderSheetData,
    createOrderSheetError,
    selectedOrderSheetOrLineSheet,
    company,
    getDenseOrderSheetSetData,
    fireCreateOrderSheet,
  ]);

  useEffect(() => {
    if (createOrderSheetData) {
      const orderSheetId = createOrderSheetData.id;
      setQuery(
        (prev) => {
          return {
            ...prev,
            orderSheetId,
            orderSheetTabIndex: createOrderSheetData.orderSheetTabs[0].index,
            lineSheetId: undefined,
          };
        },
        {
          replace: true,
        }
      );
      mutateGetDenseOrderSheetSet().then(() => {
        clearCreateOrderSheet();
      });
    }
  }, [
    createOrderSheetData,
    setQuery,
    mutateGetDenseOrderSheetSet,
    clearCreateOrderSheet,
  ]);

  const useFetchAndSetOrderSheetTabsParameter = useMemo(() => {
    if (
      getDenseOrderSheetSetData &&
      selectedOrderSheetOrLineSheet &&
      getOrderSheetRevisionsData &&
      selectedOrderSheetRevision &&
      isDenseOrderSheet(selectedOrderSheetOrLineSheet)
    ) {
      const orderSheetSet = getDenseOrderSheetSetData;
      const orderSheet = selectedOrderSheetOrLineSheet;
      return {
        data: {
          buyerCompanyId: orderSheetSet.createdByCompany.id,
          orderSheetSetId: getDenseOrderSheetSetData.id,
          orderSheetId: orderSheet.id,
          revisionNumber: selectedOrderSheetRevision.revisionNumber,
          latestRevisionNumber: latestRevision.revisionNumber,
          lineSheetSetId: orderSheetSet.lineSheetSet.id,
          lineSheetId: orderSheet.lineSheet.id,
          tabIndexes: orderSheet.orderSheetTabs.map((i) => i.index),
          orderSheetSetStatus: orderSheetSet.status,
        },
        query: sheetTabFilterQuery,
      };
    }
    return {
      data: undefined,
    };
  }, [
    getDenseOrderSheetSetData,
    selectedOrderSheetOrLineSheet,
    getOrderSheetRevisionsData,
    selectedOrderSheetRevision,
    latestRevision,
    sheetTabFilterQuery,
  ]);

  const {
    mutateListAllSheetTabs,
    mutateListAllSheetTabsRevision,
    setInflatedTabsClean,
  } = useFetchAndSetOrderSheetTabs(useFetchAndSetOrderSheetTabsParameter);

  const [isOrderSheetSaving, setIsOrderSheetSaving] = useState<boolean>(false);

  const [
    isUpdatingInflatedProductCompleted,
    setIsUpdatingInflatedProductCompleted,
  ] = useState(false);

  const orderSheetSetSaveButtonRef = useRef<{
    handleSaveOrderSheetSet: () => void;
  }>(null);

  const [
    isOrderSheetSetSaveButtonClicked,
    setIsOrderSheetSetSaveButtonClicked,
  ] = useState<boolean>(false);

  const orderSheetOrderButtonRef = useRef<{
    handleOrderOrderSheet: () => void;
  }>(null);

  const [isOrderSheetOrderButtonClicked, setIsOrderSheetOrderButtonClicked] =
    useState<boolean>(false);

  useEffect(() => {
    if (isUpdatingInflatedProductCompleted) {
      setIsUpdatingInflatedProductCompleted(false);

      /* 1. save 버튼을 클릭해서 호출된 경우 order-sheet-set-page.tsx에서 save 버튼이 클릭되었다는 상태값을 true로 변경
       * 2-1. save버튼이 클릭되었다는 상태값이 true인 경우 handleSaveOrderSheetSet()을 실행하고 상태값을 false로 변경
       * 2-2. save버튼이 클릭되었다는 상태값이 false인 경우 아무것도 하지 않음
       */
      if (
        orderSheetSetSaveButtonRef.current &&
        isOrderSheetSetSaveButtonClicked
      ) {
        orderSheetSetSaveButtonRef.current.handleSaveOrderSheetSet();
      }
      if (orderSheetOrderButtonRef.current && isOrderSheetOrderButtonClicked) {
        orderSheetOrderButtonRef.current.handleOrderOrderSheet();
      }
    }
  }, [
    isUpdatingInflatedProductCompleted,
    setIsUpdatingInflatedProductCompleted,
    orderSheetSetSaveButtonRef,
    isOrderSheetSetSaveButtonClicked,
    orderSheetOrderButtonRef,
    isOrderSheetOrderButtonClicked,
  ]);

  const handleAfterSaveOrderSheetSet = useCallback(
    async (response: CreateOrderSheetRevisionResponse) => {
      //sheetKey 변경 -> reset inflatedTabs -> tab mutate하기!
      //isDirty초기화는 의미가 없음 어차피 reset으로 다 날리니까

      await mutateListAllSheetTabs();
      await mutateListAllSheetTabsRevision();
      setInflatedTabs((prev) => {
        if (!prev) {
          return;
        }
        return {
          ...prev,
          tabs: prev.tabs.map((tab) => {
            return {
              ...tab,
              products: tab.products.map((product) => {
                return {
                  ...product,
                  stockQuantityWithOptionList:
                    product.stockQuantityWithOptionList.map(
                      (stockQuantityWithOption) => {
                        return {
                          ...stockQuantityWithOption,
                          isNew: false,
                        };
                      }
                    ),
                };
              }),
            };
          }),
        };
      });
      //setInflatedTabsClean();
    },
    [mutateListAllSheetTabs, mutateListAllSheetTabsRevision, setInflatedTabs]
  );

  const handleOrderSheetSubmit = useCallback(
    (response: SubmitOrderSheetSetV2Response) => {
      mutateGetDenseOrderSheetSet();
    },
    [mutateGetDenseOrderSheetSet]
  );

  //orderSheetSetStatusUpdater
  const updateOrderSheetStatusItems =
    useMemo((): UpdateOrderSheetStatusItem[] => {
      if (getDenseOrderSheetSetData && selectedOrderSheetOrLineSheet) {
        if (isDenseOrderSheet(selectedOrderSheetOrLineSheet)) {
          const orderSheetSet = getDenseOrderSheetSetData;
          const lineSheetSet = orderSheetSet.lineSheetSet;
          const orderSheet = selectedOrderSheetOrLineSheet;
          const lineSheet = orderSheet.lineSheet;
          return [
            {
              orderSheetSetId: orderSheetSet.id,
              id: orderSheet.id,
              name: lineSheet.name,
              suffix: orderSheet.suffix,
              status: orderSheet.status,
              createdByCompany: orderSheetSet.createdByCompany,
              issuedBy: lineSheetSet.issuedByBoutique,
              lineSheet: {
                id: lineSheet.id,
                status: lineSheet.status,
              },
              toBeSubmitted: orderSheet.toBeSubmitted,
            },
          ];
        }
      }
      return [];
    }, [getDenseOrderSheetSetData, selectedOrderSheetOrLineSheet]);

  const isLoading =
    isGetDenseOrderSheetSetLoading ||
    isGetDenseOrderSheetSetValidating ||
    isCreateOrderSheetLoading ||
    isOrderSheetSaving ||
    isGetOrderSheetRevisionsLoading;

  const isOrderSheetEditable = useMemo(() => {
    if (
      company?.type === "BUYER" &&
      selectedOrderSheetOrLineSheet &&
      isDenseOrderSheet(selectedOrderSheetOrLineSheet)
    ) {
      if (
        selectedOrderSheetRevision &&
        latestRevision &&
        // 최신 리비전인 경우에만 편집 가능
        latestRevision.revisionNumber >
          selectedOrderSheetRevision.revisionNumber
      ) {
        return false;
      }
      return ["INITIAL_DRAFT", "WORKING_DRAFT", "RESUBMIT_REQUIRED"].includes(
        selectedOrderSheetOrLineSheet.status
      );
    }
    return false;
  }, [
    company?.type,
    selectedOrderSheetOrLineSheet,
    selectedOrderSheetRevision,
    latestRevision,
  ]);

  const isOrderSheetSetOrderable = useMemo(() => {
    if (getDenseOrderSheetSetData) {
      return getDenseOrderSheetSetData.orderSheets.some((orderSheet) => {
        return ["INITIAL_DRAFT", "WORKING_DRAFT", "RESUBMIT_REQUIRED"].includes(
          orderSheet.status
        );
      });
    }
    return false;
  }, [getDenseOrderSheetSetData]);

  const isOrderSheetOrderable = useMemo(() => {
    if (getDenseOrderSheetSetData) {
      return getDenseOrderSheetSetData.orderSheets.some((orderSheet) => {
        return (
          orderSheet.id === selectedOrderSheetOrLineSheet?.id &&
          ["INITIAL_DRAFT", "WORKING_DRAFT", "RESUBMIT_REQUIRED"].includes(
            orderSheet.status
          )
        );
      });
    }
    return false;
  }, [getDenseOrderSheetSetData, selectedOrderSheetOrLineSheet]);

  const isOrderSheetCancelable = useMemo(() => {
    if (getDenseOrderSheetSetData) {
      return getDenseOrderSheetSetData.orderSheets.some((orderSheet) => {
        return (
          orderSheet.id === selectedOrderSheetOrLineSheet?.id &&
          ["ORDERED_TO_AGENCY"].includes(orderSheet.status)
        );
      });
    }
    return true;
  }, [getDenseOrderSheetSetData, selectedOrderSheetOrLineSheet]);

  const handleOrderSheetSetEditing = useCallback((isEditing: boolean) => {
    setIsOrderSheetEditing(isEditing);
  }, []);

  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  const handleFilterSelect = useCallback(
    (query: SheetTabFilterQuery) => {
      setQuery(
        (prev) => {
          const next = {
            ...prev,
            ...query,
          };
          return next;
        },
        {
          replace: true,
        }
      );
    },
    [setQuery]
  );

  const handleSheetTabFilterClear = useCallback(() => {
    setQuery(
      (prev) => {
        return {
          ...prev,
          ...EMPTY_SHEET_TAB_FILTER_QUERY,
        };
      },
      {
        replace: true,
      }
    );
  }, [setQuery]);

  useEffect(() => {
    if (
      selectedOrderSheetOrLineSheet?.id &&
      selectedOrderSheetTabOrLineSheetTab?.index !== undefined &&
      !isNaN(selectedOrderSheetTabOrLineSheetTab?.index) &&
      !isNaN(selectedOrderSheetRevision.revisionNumber)
    ) {
      filterInflatedProducts(
        sheetKey,
        selectedOrderSheetTabOrLineSheetTab.index,
        (products) => {
          return isProductFiltered(products, sheetTabFilterQuery);
        },
        sheetTabFilterQuery.orderBy || "",
        isOrderSheetDirty
      );
    }
  }, [
    sheetKey,
    selectedOrderSheetOrLineSheet?.id,
    selectedOrderSheetTabOrLineSheetTab?.index,
    selectedOrderSheetRevision.revisionNumber,
    filterInflatedProducts,
    sheetTabFilterQuery,
    isOrderSheetDirty,
  ]);

  const [isOrderRulesPanelOpen, setIsOrderRulesPanelOpen] = useState<boolean>(
    localStorage.getItem("isSheetInfoOpen") === "true"
  );

  const [isSheetInfoButtonHovered, setIsSheetInfoButtonHovered] =
    useState<boolean>(false);

  const handleOrderRulesToggle = useCallback(() => {
    setIsOrderRulesPanelOpen((prev) => !prev);
    localStorage.setItem("isSheetInfoOpen", String(!isOrderRulesPanelOpen));
  }, [isOrderRulesPanelOpen]);

  const handleOrderRulesSave = useCallback(
    (response: UpdateLineSheetSetOrderRulesResponse) => {
      mutateGetDenseOrderSheetSet(
        (prev) => {
          if (prev) {
            return produce(prev, (draft) => {
              draft.lineSheetSet.orderRules = response.orderRules;
            });
          }
        },
        { revalidate: false }
      );
    },
    [mutateGetDenseOrderSheetSet]
  );

  // 오더 협의 중(ORDERED_TO_BOUTIQUE)에는 에이전시와 부티크가 컨펌 수량·가격을 입력해 저장할 수 있다.
  const isConfirmedEditable = useMemo(() => {
    return (
      (company?.type === "AGENCY" || company?.type === "BOUTIQUE") &&
      selectedOrderSheetOrLineSheet !== undefined &&
      isDenseOrderSheet(selectedOrderSheetOrLineSheet) &&
      selectedOrderSheetOrLineSheet.status === "ORDERED_TO_BOUTIQUE"
    );
  }, [company?.type, selectedOrderSheetOrLineSheet]);

  const isConditionEditable = useMemo(() => {
    return (
      (isOrderSheetEditable &&
        getDenseOrderSheetSetData?.status !== "RESUBMIT_REQUIRED") ||
      isConfirmedEditable
    );
  }, [
    getDenseOrderSheetSetData?.status,
    isConfirmedEditable,
    isOrderSheetEditable,
  ]);

  const handleUpdateOrderSheetStatus: OrderSheetStatusUpdaterProps["onUpdate"] =
    useCallback(
      (data) => {
        if (isBulkUpdateOrderSheetStatusResponse(data)) {
          mutateGetDenseOrderSheetSet();
        }
      },
      [mutateGetDenseOrderSheetSet]
    );

  const {
    isLoading: isBulkUpdateOrderSheetStatusLoading,
    data: bulkUpdateOrderSheetStatusData,
    error: bulkUpdateOrderSheetStatusError,
    fire: fireBulkUpdateOrderSheetStatus2,
    clear: clearBulkUpdateOrderSheetSetStatus,
  } = useBulkUpdateOrderSheetStatus();

  useEffect(() => {
    if (bulkUpdateOrderSheetStatusError) {
      showError(bulkUpdateOrderSheetStatusError);
    }
    if (bulkUpdateOrderSheetStatusData) {
      // 오더 취소가 성공하면 다시 데이터를 가져와서 새로고침 처리를 함
      bulkUpdateOrderSheetStatusData.orderSheets.map((orderSheet) => {
        if (
          orderSheet.id === selectedOrderSheetOrLineSheet?.id &&
          orderSheet.status === "WORKING_DRAFT"
        ) {
          mutateGetDenseOrderSheetSet();
        }
      });
    }
  }, [
    bulkUpdateOrderSheetStatusError,
    showError,
    bulkUpdateOrderSheetStatusData,
    selectedOrderSheetOrLineSheet,
    mutateGetDenseOrderSheetSet,
  ]);

  const [isKrw, setIsKrw] = useRecoilState(isOrderSheetPageCurrencyKrw);
  // 원화 라인시트(세트 통화 또는 선택한 라인시트 통화가 KRW)는 바꿀 통화가 없다 — 통화 변환을 감추고,
  // 다른 세트에서 켜 둔 원화 보기가 남아 원화 가격을 다시 환산하지 않게 끈다
  const isKrwLineSheet =
    getLineSheetSetData?.currency === "KRW" || currency === "KRW";
  useEffect(() => {
    if (isKrwLineSheet && isKrw) {
      setIsKrw(false);
    }
  }, [isKrwLineSheet, isKrw, setIsKrw]);

  const lineSheetSetName = getDenseOrderSheetSetData
    ? `#${getDenseOrderSheetSetData?.lineSheetSet.id} ${getDenseOrderSheetSetData?.lineSheetSet.name}`
    : "";

  const totalOrderQuantity =
    inflatedTabs?.tabs[0]?.products?.reduce((acc, product) => {
      const orderQuantityWithOptionList =
        product.orderQuantityWithOptionList || [];
      const productTotalQuantity = orderQuantityWithOptionList.reduce(
        (sum, option) => sum + (option.quantity || 0),
        0
      );
      return acc + productTotalQuantity;
    }, 0) || 0;

  return (
    <>
      <Helmet>
        <title>Pathrade | Order Sheets</title>
        <link
          rel="canonical"
          href={getCanonicalUrl()}
        />
        <meta property="og:title" content="Pathrade | Order Sheets" />
        <meta
          property="og:url"
          content={getCanonicalUrl()}
        />
      </Helmet>
      <Flex
        flexDirection={"column"}
        height={"100%"}
        onClick={() => {
          gridRef.current?.api.stopEditing();
        }}
      >
        <Flex
          flexDirection={"row"}
          p={"8px 16px 8px 16px"}
          gap={"12px"}
          alignItems={"center"}
          borderBottom={"0.5px solid var(--gray, #D4D4D1)"}
          background={"var(--white, #FFF)"}
        >
          <Box
            flexShrink={"0"}
            flexGrow={"0"}
            alignItems={"center"}
            display={"flex"}
          >
            <AppIconButton
              variant={"ghost"}
              ariaLabel={"go to previous page"}
              size={"medium"}
              icon={"left"}
              onClick={async () => {
                const result = await confirmDiscardChanges();
                if (result) {
                  navigate(-1, {
                    defaultPage: "/line-sheets",
                  });
                }
              }}
            />
          </Box>
          <Text textStyle={"body-lg-bold"}>{lineSheetSetName}</Text>
          {getDenseOrderSheetSetData &&
            (getDenseOrderSheetSetData.status === "INITIAL_DRAFT" ? (
              // <AppLabel
              //   nameKey={`order_sheet.status.${getDenseOrderSheetSetData.status.toLowerCase()}`}
              //   level={"LIGHT"}
              // />
              <OrderStatusBadge status={getDenseOrderSheetSetData.status} />
            ) : (
              // <AppOrderSheetStatusLabel
              //   value={getDenseOrderSheetSetData.status}
              // />
              <OrderStatusBadge status={getDenseOrderSheetSetData.status} />
            ))}
          <Spacer />
          <Flex
            flexDirection={"row"}
            gap={"8px"}
            wrap={"wrap"}
            flexGrow={"0"}
            width={"256px"}
            minWidth={"256px"}
            justifyContent={"flex-end"}
          >
            {(company?.type === "BUYER" ||
              ((company?.type === "AGENCY" || company?.type === "BOUTIQUE") &&
                selectedOrderSheetOrLineSheet &&
                isDenseOrderSheet(selectedOrderSheetOrLineSheet) &&
                selectedOrderSheetOrLineSheet.status ===
                  "ORDERED_TO_BOUTIQUE")) && (
              <>
                <Flex
                  flexDirection={"row"}
                  gap={"8px"}
                  wrap={"wrap"}
                  flexGrow={"0"}
                >
                  {isOrderSheetEditable && (
                    <AppButton
                      width={"80px"}
                      isDisabled={
                        (!isOrderSheetDirty &&
                          sheetStatisticState?.statistic
                            .totalOrderedQuantity === 0) ||
                        !isOrderSheetEditable
                      }
                      onClick={() => {
                        gridRef.current?.api.stopEditing(true);
                        if (!inflatedTabs) {
                          return;
                        }
                        setInflatedTabs((prev) => {
                          if (!prev) {
                            return;
                          }
                          return {
                            ...prev,
                            tabs: prev.tabs.map((tab) => {
                              return {
                                ...tab,
                                isDirty:
                                  initialSheetStatistics?.statistic
                                    .totalOrderedQuantity ===
                                  sheetStatisticState?.statistic
                                    .totalOrderedQuantity
                                    ? false
                                    : true,
                                products: tab.products.map((product) => {
                                  return {
                                    ...product,
                                    orderQuantityWithOptionList:
                                      product.orderQuantityWithOptionList.map(
                                        (orderQuantityWithOption) => {
                                          return {
                                            ...orderQuantityWithOption,
                                            quantity: 0,
                                          };
                                        }
                                      ),
                                  };
                                }),
                              };
                            }),
                          };
                        });
                        mutateGetDenseOrderSheetSet();
                      }}
                    >
                      {tTitle("common:reset_order")}
                    </AppButton>
                  )}
                  <OrderSheetSetSaveButton
                    orderSheetSetId={orderSheetSetId}
                    orderSheet={
                      selectedOrderSheetOrLineSheet &&
                      isDenseOrderSheet(selectedOrderSheetOrLineSheet)
                        ? selectedOrderSheetOrLineSheet
                        : undefined
                    }
                    revisionNumber={selectedOrderSheetRevision?.revisionNumber}
                    isOrderSheetEditable={isOrderSheetEditable}
                    isOrderSheetEditing={isOrderSheetEditing}
                    isAllOrderSheetsOrdered={
                      getDenseOrderSheetSetData
                        ? getDenseOrderSheetSetData.orderSheets.every(
                            isOrderedToAgency
                          )
                        : false
                    }
                    isConfirmedEditable={isConfirmedEditable}
                    onUpdatingInflatedProductCompleted={
                      setIsUpdatingInflatedProductCompleted
                    }
                    onOrderSheetSetSaveButtonClicked={
                      setIsOrderSheetSetSaveButtonClicked
                    }
                    onSaveLoading={setIsOrderSheetSaving}
                    onAfterSave={handleAfterSaveOrderSheetSet}
                    gridRef={gridRef}
                    ref={orderSheetSetSaveButtonRef}
                  />
                  {isOrderSheetOrderable && (
                    <OrderSheetOrderButton
                      isOrderSheetEditing={isOrderSheetEditing}
                      isAllOrderSheetsOrdered={
                        getDenseOrderSheetSetData
                          ? getDenseOrderSheetSetData.orderSheets.every(
                              isOrderedToAgency
                            )
                          : false
                      }
                      isOrderSheetSetOrderable={isOrderSheetOrderable}
                      orderSheetSetId={orderSheetSetId}
                      orderedQuantity={totalOrderQuantity}
                      orderSheet={
                        selectedOrderSheetOrLineSheet !== undefined &&
                        isDenseOrderSheet(selectedOrderSheetOrLineSheet)
                          ? selectedOrderSheetOrLineSheet
                          : undefined
                      }
                      revisionNumber={
                        selectedOrderSheetRevision?.revisionNumber
                      }
                      moq={getOrderMoq(getLineSheetSetData)}
                      canOrderByUnit={canOrderByUnit(getLineSheetSetData)}
                      // onSave={handleAfterSaveOrderSheetSet}
                      onUpdatingInflatedProductCompleted={
                        setIsUpdatingInflatedProductCompleted
                      }
                      onOrderSheetOrderButtonClicked={
                        setIsOrderSheetOrderButtonClicked
                      }
                      onSubmit={handleOrderSheetSubmit}
                      gridRef={gridRef}
                      ref={orderSheetOrderButtonRef}
                    />
                  )}
                  {isOrderSheetCancelable && (
                    <AppButton
                      key={"OrderSheetFormAction_Cancel"}
                      width={"80px"}
                      variant={"white"}
                      isDisabled={!isOrderSheetCancelable}
                      onClick={() => {
                        fireBulkUpdateOrderSheetStatus2(
                          {
                            by: identity!.company!.type,
                            companyId: identity!.company!.id,
                            orderSheetSetId,
                          },
                          {
                            status: "WORKING_DRAFT",
                            orderSheetIds: [selectedOrderSheetOrLineSheet!.id],
                          }
                        );
                      }}
                    >
                      {tTitle("common:cancel_submit")}
                    </AppButton>
                  )}
                </Flex>
                {/*<OrderSheetRevisionSelector*/}
                {/*  minWidth={"100%"}*/}
                {/*  values={orderSheetRevisionSelectValues}*/}
                {/*  value={selectedOrderSheetRevision}*/}
                {/*  onSelect={handleSelectOrderSheetRevision}*/}
                {/*/>*/}
              </>
            )}
          </Flex>
        </Flex>
        <Flex
          paddingX={"16px"}
          paddingY={"12px"}
          background={"#FFFFFF"}
          boxShadow={"0 0 0 0.5px #D4D4D1"}
        >
          {getDenseOrderSheetSetData && (
            <SheetTabFilter
              sheetKey={sheetKey}
              lineSheetSetId={getDenseOrderSheetSetData?.lineSheetSet.id!}
              // 고른 것이 오더시트면 그 id 가 아니라 바탕이 된 라인시트 id 를 넘겨야 필터 옵션이 나온다.
              lineSheetId={
                selectedOrderSheetOrLineSheet
                  ? isDenseOrderSheet(selectedOrderSheetOrLineSheet)
                    ? selectedOrderSheetOrLineSheet.lineSheet.id
                    : selectedOrderSheetOrLineSheet.id
                  : undefined
              }
              tabIndex={
                selectedOrderSheetTabOrLineSheetTab
                  ? selectedOrderSheetTabOrLineSheetTab.index
                  : -1
              }
              query={query}
              setQuery={setQuery}
              onFilterSelect={handleFilterSelect}
              onClear={handleSheetTabFilterClear}
              queryCount={queryCount}
              setQueryCount={(count: number) => setQueryCount(count)}
            />
          )}
          <Flex
            position={"relative"}
            zIndex={"2"}
            height={"28px"}
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <AppSelect<string>
              borderWidth={"0px"}
              isMultiple={false}
              isClearable={false}
              name={tTitle("sort_by")}
              value={query.orderBy}
              options={
                [
                  { name: tTitle("default"), value: "default" },
                  { name: tTitle("price_asc"), value: "price_asc" },
                  { name: tTitle("price_desc"), value: "price_desc" },
                ] as AppSelectOption<string>[]
              }
              onSelect={(value) =>
                setQuery(
                  (prev) => {
                    return {
                      ...prev,
                      orderBy: value,
                    };
                  },
                  {
                    replace: true,
                  }
                )
              }
            />
          </Flex>
        </Flex>
        <Flex>
          <Flex
            position={"relative"}
            zIndex={"2"}
            background={"#FCFCFE"}
            flexDirection={"row"}
            alignItems={"center"}
            width={"652px"}
            gap={"8px"}
            pl={"16px"}
            marginY={"6px"}
          >
            <OrderSheetOrLineSheetSelector
              minWidth={"326px"}
              values={orderSheetOrLineSheetSelectValues}
              value={selectedOrderSheetOrLineSheet}
              onSelect={handleSelectOrderSheetOrLineSheet}
            />
          </Flex>
          {identity?.company?.type === "AGENCY" && (
            <AppTabSelect
              name={tTitle("line_sheet_tab")}
              value={selectedOrderSheetTabOrLineSheetTab}
              values={orderSheetTabOrLineSheetTabSelectValues}
              onSelect={handleSelectOrderSheetTabOrLineSheetTab}
            />
          )}
        </Flex>

        <Flex flexGrow={"1"} flexDirection={"column"} minHeight={0}>
          <Flex
            flexDirection={"row"}
            padding={"2px 12px 8px 16px"}
            alignItems={"center"}
            gap={"8px"}
          >
            <Box
              onMouseEnter={() => setIsSheetInfoButtonHovered(true)}
              onMouseLeave={() => setIsSheetInfoButtonHovered(false)}
            >
              <AppButton
                variant={"normal"}
                onClick={handleOrderRulesToggle}
                chakraProps={{
                  p: "4px 8px",
                  border: isSheetInfoButtonHovered
                    ? "0.5px solid #1272EF"
                    : isOrderRulesPanelOpen
                    ? "0.5px solid #00417D"
                    : "0.5px solid #8F8F8C",
                }}
              >
                <Flex gap={"4px"} alignItems={"center"}>
                  <Text
                    textStyle="body-sm-bold"
                    color={
                      isSheetInfoButtonHovered
                        ? "#1272EF"
                        : isOrderRulesPanelOpen
                        ? "#00417D"
                        : "#8F8F8C"
                    }
                  >
                    {tTitle("sheet_info")}
                  </Text>
                  <SVG
                    src={
                      isOrderRulesPanelOpen
                        ? "/icons-v1_1/chevron-double-left.svg"
                        : "/icons-v1_1/chevron-double-right.svg"
                    }
                    width={"16px"}
                    height={"16px"}
                    color={
                      isSheetInfoButtonHovered
                        ? "#1272EF"
                        : isOrderRulesPanelOpen
                        ? "#00417D"
                        : "#8F8F8C"
                    }
                  />
                </Flex>
              </AppButton>
            </Box>
            <Divider
              orientation={"vertical"}
              marginX={"5px"}
              borderColor={"#D4D4D1"}
            />
            <OrderSheetAdjustPriceFragment
              orderSheetSetId={orderSheetSetId}
              orderSheetId={
                selectedOrderSheetOrLineSheet &&
                isDenseOrderSheet(selectedOrderSheetOrLineSheet)
                  ? selectedOrderSheetOrLineSheet.id
                  : -1
              }
              revisionNumber={selectedOrderSheetRevision.revisionNumber}
              tabIndex={
                selectedOrderSheetTabOrLineSheetTab
                  ? selectedOrderSheetTabOrLineSheetTab.index
                  : -1
              }
              selectedProductIds={selectedProductIds}
              isConfirmedEditable={isConfirmedEditable}
              commission={getDenseOrderSheetSetData?.lineSheetSet?.commission}
              commissionType={
                getDenseOrderSheetSetData?.lineSheetSet?.commissionType
              }
            />
            <Divider
              orientation={"vertical"}
              marginX={"5px"}
              borderColor={"#D4D4D1"}
            />
            <FormControl
              display="flex"
              alignItems="center"
              width={"fit-content"}
            >
              <FormLabel htmlFor="only_rows_with_qty" mb="0">
                <Text textStyle={"body-sm-regular"}>
                  {t("only_rows_with_qty")}
                </Text>
              </FormLabel>
              <Switch
                id="only_rows_with_qty"
                isChecked={query.quantity__gte === 1}
                onChange={(event) => {
                  handleFilterSelect({
                    ...query,
                    quantity__gte: event.target.checked ? 1 : undefined,
                  });
                }}
              />
            </FormControl>
            <Divider
              orientation={"vertical"}
              marginX={"5px"}
              borderColor={"#D4D4D1"}
            />
            {getLineSheetSetData && !isKrwLineSheet && (
              <Flex gap={"8px"} alignItems={"center"}>
                <Flex gap={"2px"} alignItems={"center"}>
                  <Text textStyle={"body-sm-regular"} color={"#444444"}>
                    {tTitle("currency_change")}
                  </Text>
                  <Tooltip label={tSentence("floating_bill_board_bar_tooltip")}>
                    <Box>
                      <SVG
                        src={"/icons-v1_1/info-circle.svg"}
                        width={"16px"}
                        height={"16px"}
                      />
                    </Box>
                  </Tooltip>
                </Flex>
                <CurrencySwitch
                  checked={isKrw}
                  onChange={(checked: boolean) => setIsKrw(checked)}
                />
              </Flex>
            )}
            {isConfirmedEditable && (
              <>
                <Spacer />
                <AppButton
                  onClick={() => {
                    setIsAddLineSheetProductOpen(true);
                  }}
                  width={"96px"}
                >
                  <Flex gap={"4px"} alignItems={"center"}>
                    <SVG
                      src={"/icons-v1_1/plus.svg"}
                      width={"16px"}
                      height={"16px"}
                      color={"#8F8F8C"}
                    />
                    <Text textStyle="body-sm-bold" color={"#444444"}>
                      상품 추가
                    </Text>
                  </Flex>
                </AppButton>
              </>
            )}
          </Flex>
          <Flex flexGrow={1} flexDirection={"row"} gap={"8px"} minHeight={0}>
            {isOrderRulesPanelOpen && (
              <Box height={"100%"} minHeight={0}>
                <SheetInfoPanel
                  onClose={handleOrderRulesToggle}
                  showMoqNotice
                  lineSheetSet={getLineSheetSetData}
                  orderSheetSet={
                    getDenseOrderSheetSetData as unknown as OrderSheetSet
                  }
                />
              </Box>
            )}
            {selectedOrderSheetOrLineSheet &&
            getDenseOrderSheetSetData &&
            isOrderedToAgency(getDenseOrderSheetSetData) &&
            isLineSheet(selectedOrderSheetOrLineSheet) ? (
              <LineSheetTab
                lineSheetSetId={
                  getDenseOrderSheetSetData?.lineSheetSet?.id || -1
                }
                lineSheetId={selectedOrderSheetOrLineSheet.id}
                tabIndex={
                  selectedOrderSheetTabOrLineSheetTab
                    ? selectedOrderSheetTabOrLineSheetTab.index
                    : -1
                }
                isLoading={isLoading}
                onSelectedProductIdsChange={setSelectedProductIds}
                onGridReady={handleGridReady}
              />
            ) : getDenseOrderSheetSetData &&
              [
                "ORDERED_TO_BOUTIQUE",
                "RESUBMIT_REQUIRED",
                "RESUBMIT_COMPLETED",
                "BOUTIQUE_CONFIRMED",
                "BUYER_CONFIRMED",
                "TRANSPORTATION_ING",
                "TRANSPORTATION_COMPLETED",
                "COMPLETED"
              ].includes(getDenseOrderSheetSetData.status) ? (
              <AgencyOrderSheetEditConfirmInfo
                isLoading={isLoading}
                sheetKey={sheetKey}
                lineSheetSetId={getLineSheetSetData?.id || -1}
                tabIndex={
                  selectedOrderSheetTabOrLineSheetTab
                    ? selectedOrderSheetTabOrLineSheetTab.index
                    : -1
                }
                isOrderEditable={isOrderSheetEditable}
                isConfirmedEditable={isConfirmedEditable}
                isConditionEditable={isConditionEditable}
                onEditing={handleOrderSheetSetEditing}
                onSelectedProductIdsChange={setSelectedProductIds}
                onGridReady={handleGridReady}
                onUpdatingInflatedProductComplete={
                  setIsUpdatingInflatedProductCompleted
                }
                orderSheetSet={getDenseOrderSheetSetData}
                selectOrderSheetId={
                  selectedOrderSheetOrLineSheet &&
                  isDenseOrderSheet(selectedOrderSheetOrLineSheet)
                    ? selectedOrderSheetOrLineSheet.id
                    : -1
                }
                revisionNumber={selectedOrderSheetRevision?.revisionNumber}
              />
            ) : (
              <OrderSheetTab
                isLoading={isLoading}
                sheetKey={sheetKey}
                lineSheetSetId={getLineSheetSetData?.id || -1}
                tabIndex={
                  selectedOrderSheetTabOrLineSheetTab
                    ? selectedOrderSheetTabOrLineSheetTab.index
                    : -1
                }
                isOrderEditable={isOrderSheetEditable}
                isConfirmedEditable={isConfirmedEditable}
                isConditionEditable={isConditionEditable}
                onEditing={handleOrderSheetSetEditing}
                onSelectedProductIdsChange={setSelectedProductIds}
                onGridReady={handleGridReady}
                onUpdatingInflatedProductComplete={
                  setIsUpdatingInflatedProductCompleted
                }
                orderSheetSet={getDenseOrderSheetSetData}
                selectOrderSheetId={
                  selectedOrderSheetOrLineSheet &&
                  isDenseOrderSheet(selectedOrderSheetOrLineSheet)
                    ? selectedOrderSheetOrLineSheet.id
                    : -1
                }
                revisionNumber={selectedOrderSheetRevision?.revisionNumber}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      <AddLineSheetProductModal
        isOpen={isAddLineSheetProductOpen}
        onCancel={async () => {
          setIsAddLineSheetProductOpen(false);
        }}
        onClose={async () => {
          setIsAddLineSheetProductOpen(false);
          await mutateListAllSheetTabs();
        }}
        lineSheetSetId={getLineSheetSetData?.id || -1}
        lineSheetId={
          useFetchAndSetOrderSheetTabsParameter.data?.lineSheetId || -1
        }
        lineSheetTabIndex={
          selectedOrderSheetTabOrLineSheetTab
            ? selectedOrderSheetTabOrLineSheetTab.index
            : -1
        }
      />
    </>
  );
}

// OrderSheetSetPage.whyDidYouRender = true;

export default OrderSheetSetPage;
